import {
  CohortSwitcherCohortsDocument,
  ResourceType,
  usePathCreatorAssignRoleMutation,
  usePathCreatorCohortsLazyQuery,
  usePathCreatorCreateCohortMutation,
  usePathCreatorCreatePathMutation,
  usePathCreatorResourcesLazyQuery
} from "@pathwright/graphql-client/src/generated"
import { massageCohort } from "./CohortSelector"
import { massageResource } from "./ResourceSelector"
import {
  PathCreatorResult,
  PathCreatorValidValues,
  SelectedCohort,
  SelectedResource,
  isExistingItem
} from "./state"

function usePathCreatorFlowSubmit() {
  const [createPath] = usePathCreatorCreatePathMutation()
  const [createCohort] = usePathCreatorCreateCohortMutation()
  const [assignRole] = usePathCreatorAssignRoleMutation()
  const [reloadResources] = usePathCreatorResourcesLazyQuery()
  const [reloadCohorts] = usePathCreatorCohortsLazyQuery()

  return async function ({
    targetUserId,
    resource,
    cohort
  }: PathCreatorValidValues & {
    targetUserId: number
  }): Promise<PathCreatorResult> {
    let existingResource: SelectedResource | null = isExistingItem(resource)
      ? resource
      : null
    let existingCohort: SelectedCohort | null = isExistingItem(cohort)
      ? cohort
      : null

    // When create enrolls the cohort role, skip the follow-up assign for that cohort.
    let enrolledCohortRoleViaCreate = false

    if (!existingResource) {
      // Creating a new resource + cohort.
      const result = await createPath({
        variables: {
          name: resource.name,
          image: resource.image || "",
          resourceType: ResourceType.Course,
          cohortName: cohort.name,
          groupRole: cohort.role || undefined
        }
      })

      // Set the resource id.
      if (result.errors) {
        throw Error(result.errors[0].toString())
      } else {
        existingResource = massageResource(result.data!.createResource!)
        existingCohort = massageCohort(result.data!.createResource!.cohort!)
        enrolledCohortRoleViaCreate = !!cohort.role

        // We created a resource, so lets reload the available resources in the Path Creator.
        // No need (or desire) to await the promise.
        reloadResources({ fetchPolicy: "network-only" })
      }
    } else if (!existingCohort) {
      // Creating a new cohort.
      const result = await createCohort({
        variables: {
          name: cohort.name,
          resourceId: existingResource.id,
          role: cohort.role || undefined
        },
        // Keep CohortSwitcher list in sync with Path Creator (new cohorts).
        refetchQueries: [CohortSwitcherCohortsDocument]
      })

      if (result.errors) {
        throw Error(result.errors[0].toString())
      } else {
        existingCohort = massageCohort(result.data!.createCohort!)
        enrolledCohortRoleViaCreate = !!cohort.role

        // We created a cohort, so lets reload the available cohorts in the Path Creator.
        // No need (or desire) to await the promise.
        reloadCohorts({
          variables: {
            // There should always be a selected resource at this point, but TS doesn't now that
            resourceId: existingResource.id,
            targetUserId
          },
          fetchPolicy: "network-only"
        })
      }
    }

    let assignRolePromises: Promise<any>[] = []

    // Assign the source cohort role (Design).
    // Only when a local source cohort exists and the user does not already have the role.
    if (
      resource.role &&
      existingResource.sourceCohort &&
      !existingResource.sourceCohort.role
    ) {
      assignRolePromises.push(
        assignRole({
          variables: {
            cohorts: [existingResource.sourceCohort.id],
            user: targetUserId,
            role: resource.role
          }
        })
      )
    }

    // Assign the cohort role only when selecting an existing cohort
    // (create already enrolled via role / group_role).
    if (cohort.role && !enrolledCohortRoleViaCreate) {
      assignRolePromises.push(
        assignRole({
          variables: {
            cohorts: [existingCohort.id],
            user: targetUserId,
            role: cohort.role
          }
        })
      )
    }

    // Wait for all role assignments to complete.
    await Promise.all(assignRolePromises)

    // Ensure we've set the roles on the existing items.
    existingCohort.role = cohort.role
    existingResource.role = resource.role
    if (existingResource.sourceCohort) {
      existingResource.sourceCohort.role = resource.role
    }

    return {
      resource: existingResource,
      cohort: existingCohort
    }
  }
}

export default usePathCreatorFlowSubmit
